import { IDirectionCount } from "./DirectionCount";
import { IPositionCount } from "./PositionCount";
import {
  ABB_PLAY,
  ABB_STOP,
  ABB_REFRESH,
  ABB_WAITING,
} from "../../utilities/IconConstants";
import { Status } from "../sample/Status";
import { ITestplanData } from "../sample/SampleViewData";
import _ from "lodash";

export interface IStatus {
  id: string;
  name: string;
}

// limits
export interface IValueRange {
  id: string;
  name: string;
}

// new
export interface ICount {
  completedCounts: number;
  totalCounts: number;
}
export interface IMeasurementProperties {
  uniqueKey?: any;
  id: string;
  name: string;
  unit: string;
  directionId: string;
  avg?: string;
  decimals: number;
}

interface IDirectionMean {
  directionId: string;
  mean: number;
  propertyId: string;
}

export interface IReadings {
  count: number;
  directionId: string;
  position: number;
  propertyId: string;
  propertyName?: string;
  value?: any;
  valueRange: IValueRange;
  positionName?: string;
  directionMeans?: IDirectionMean[];
  decimals?: number;
}

// new
export interface IIconStatus {
  name: string;
  color: string;
}

// direction data
export interface ICountData {
  position: number;
  index: number;
  positionName: string;
  directions: IDirectionCountChildData[];
  selected: boolean;
}

export interface IDirectionCountChildData {
  id: string;
  name: string;
  completedCounts: number;
  totalCounts: number;
  selected: boolean;
  maxCount: number;
  skipped?: number;
  skippedDetails?:number[],
  measurementCounts?: number[],
  order: number
}

// statistics
export interface IStatistics {
  covariance: number;
  directionId: string;
  mean: number;
  median: number;
  position: number;
  propertyId: string;
  standardDeviation: number;
  propertyName?: string;
  decimals?: number;
}

export interface IPropertyMean {
  propertyId: string;
  mean: number;
}

export interface IPositionMeans {
  propertyId: string;
  position: number;
  mean: number;
}

export interface NextMeasuringPositionDirection {
  position: number;
  directionId: string;
  count: number;
}

export interface maxCountMappingNonAutoline {
  id: string;
  maxCount: number;
  position: number;
}
export interface IInstrumentDetails {
  id: string;
  name: string;
  count: ICount;
  properties: IMeasurementProperties[];
  readings: IReadings[];
  countData: ICountData[];
  completionPercentage: string;
  iconStatus: IIconStatus;
  instrumentStatus: IStatus;
  status: { name: string; color: string };
  positionCount: IPositionCount[];
  statistics: IStatistics[];
  propertyMeans: IPropertyMean[];
  positionMeans: IPositionMeans[];
  setPositionVal?: number;
  isAutoLine?: boolean;
  directionCounts?: IDirectionCount[];
  readingData: any[];
  currentMeasuringProperty?: string;
  currentMeasuringPosition?: number;
  allDirections: any[];
  checkInstrumentInUse: any;
  nextMeasuringPositionDireaction?: NextMeasuringPositionDirection;
  setSelectedDirection(position: number, id: string): void;
  updateDirectionCount(
    position: number,
    directionId: string,
    completedCounts: number
  ): void;
  setCount(count: ICount): void;
  setAverage(
    propertyId: string,
    directionId: string,
    position: number,
    statistic: any
  ): void;
  setPosition(index: number): void;
  setReadings(
    count: any,
    directionId: string,
    position: number,
    propertyId: string,
    value: any,
    valueRange: IValueRange,
    positionName?: string,
    decimals?: number
  ): void;
  setStatus(instrumentStatus: IStatus, instrumentSampleStatus?: IStatus): void;
  setMeasuredPosition(position: number): void;
  setAutolineCount(count: ICount): void;
  setCurrentMeasuringProperty(propertyId: string, position: number): void;
  setPositionCount(positionCount: IPositionCount[]): void;
  setDirectionCount(positionId: any, directionId: any, compCount: number): void;
}

interface Dictionary<T> {
  [Key: string]: T;
}

export class InstrumentDetails implements IInstrumentDetails {
  id: string;
  name: string;
  count: ICount;
  directionCounts: IDirectionCount[];
  positionCount: IPositionCount[];
  properties: IMeasurementProperties[];
  readings: IReadings[];
  countData: ICountData[];
  completionPercentage: string;
  iconStatus: IIconStatus;
  instrumentStatus: IStatus;
  instrumentSampleStatus?: IStatus;
  status: { name: string; color: string };
  statistics: IStatistics[];
  propertyMeans: IPropertyMean[];
  positionMeans: IPositionMeans[];
  setPositionVal: number;
  isAutoLine: boolean;
  currentMeasuringProperty?: string;
  currentMeasuringPosition?: number;
  private readonly oldPositionCount: IPositionCount[];
  readingData: any[];
  allDirections: any[];
  measurementId?: string;
  testPlan?: ITestplanData;
  checkInstrumentInUse: any;
  nextMeasuringPositionDireaction?: NextMeasuringPositionDirection;
  currentSampleStatus?: IStatus;

  constructor(
    id: string,
    name: string,
    count: ICount,
    directionCounts: IDirectionCount[],
    properties: IMeasurementProperties[],
    readings: IReadings[],
    instrumentStatus: IStatus,
    positionCount: IPositionCount[],
    statistics: IStatistics[],
    propertyMeans: IPropertyMean[],
    positionMeans: IPositionMeans[],
    isAutoLine: boolean,
    measurementId?: string,
    instrumentSampleStatus?: IStatus,
    testPlan?: ITestplanData,
    currentSampleStatus?: IStatus,
    checkInstrumentInUse?: any,
  ) {
    this.id = id;
    this.name = name;
    this.directionCounts = directionCounts;
    this.properties = properties;
    this.isAutoLine = isAutoLine;
    this.measurementId = measurementId;
    this.instrumentSampleStatus = instrumentSampleStatus;
    this.testPlan = testPlan;
    this.currentSampleStatus = currentSampleStatus;
    if (readings === undefined || readings === null) {
      this.readings = [];
    } else {
      this.readings = readings;
    }

    if (statistics === undefined || statistics === null) {
      this.statistics = [];
    } else {
      this.statistics = statistics;
    }

    this.countData = [
      {
        position: -1,
        index: -1,
        positionName: "",
        directions: [
          {
            id: "All",
            name: "All",
            completedCounts: 0,
            totalCounts: 0,
            selected: true,
            maxCount: 0,
            skipped: 0,
            order:0
          },
        ],
        selected: true,
      },
    ];
    this.iconStatus = { name: ABB_PLAY, color: "blue" };
    if (positionCount === null || positionCount === undefined) {
      this.positionCount = [];
    } else {
      this.positionCount = positionCount;
      this.oldPositionCount = positionCount;
      if (!this.isAutoLine) {
        if (this.positionCount.length > 0) {
          this.positionCount[0].selected = true;
        }
      }
    }

    this.propertyMeans = propertyMeans || [];
    this.positionMeans = positionMeans || [];
    this.setPositionVal = 0;
    if (this.readingData === undefined) {
      this.readingData = [];
    }

    this.setCount(count);
    this.setAllDirections();
    this.setStatus(instrumentStatus, instrumentSampleStatus);
    if (this.isAutoLine) {
      this.setPosData();
    }

    this.allDirections = [];
    this.setDirections();
    this.checkInstrumentInUse = checkInstrumentInUse;
    this.nextMeasuringPositionDireaction = !this.isAutoLine
      ? this.getNextMeasuringDetails(
          this.countData,
          this.allDirections,
          this.testPlan,
          this.readings
        )
      : { position: 0, count: 0, directionId: "" };
  }

  public getNextMeasuringDetails = (
    countData: ICountData[],
    allDirections: any[],
    testPlan: ITestplanData,
    readings:IReadings[]
  ): NextMeasuringPositionDirection => {
    let position = 0;
    let directionId = "";
    let count = 0;
    if (testPlan && testPlan.isPDC) {
      const findMeasuringPosition = countData.find(
        (el: ICountData) =>
          el.directions[0].totalCounts !== el.directions[0].completedCounts
      );
      if (findMeasuringPosition) {
        findMeasuringPosition.directions?.sort((a:any,b:any)=>{return a.order - b.order});
        const findDirectioninPosition = findMeasuringPosition.directions.find(
          (elements: IDirectionCountChildData) =>
            elements.name !== "All" &&
            elements.totalCounts !== elements.completedCounts
        );
        if (findDirectioninPosition) {
          let findCount = readings.filter((x:IReadings)=>x.directionId === findDirectioninPosition.id && x.position === findMeasuringPosition.position).map((x:IReadings)=>x.count);
          let totalCount = Array.from({length: findDirectioninPosition.totalCounts}, (_, index) => index + 1);
          let nextCount = 1
          if(findCount.length){
            let uniqueDat = _.uniq(findCount);
            let diffData =_.difference(totalCount, uniqueDat);
            nextCount = (diffData || []).find((x:number)=>!findDirectioninPosition.skippedDetails.includes(x));
          } else if(findDirectioninPosition.skippedDetails && findDirectioninPosition.skippedDetails.length){
            nextCount = (totalCount || []).find((x:number)=>!findDirectioninPosition.skippedDetails.includes(x));
          }

          position = findMeasuringPosition.position;
          directionId = findDirectioninPosition.id;
          count = nextCount;
        }
      }
    } else {
      allDirections?.sort((a:any,b:any)=>{return a.order - b.order});
      for (let i = 0; i < allDirections.length; i++) {
        let isBreak = false;
        for (let j = 0; j < countData.length; j++) {
          countData[j].directions?.sort((a:any,b:any)=>{return a.order - b.order});
          const findDirectionData = countData[j].directions.find(
            (elements: IDirectionCountChildData) =>
              elements.id === allDirections[i].id
          );
          if (
            findDirectionData &&
            findDirectionData.completedCounts !== findDirectionData.totalCounts
          ) {
            let findCount = readings.filter((x:IReadings)=>x.directionId === findDirectionData.id && x.position === countData[j].position).map((x:IReadings)=>x.count);
            let totalCount = Array.from({length: findDirectionData.totalCounts}, (_, index) => index + 1);
            let nextCount = 1
            if(findCount.length){
              let uniqueDat = _.uniq(findCount);
              let diffData =_.difference(totalCount, uniqueDat);
              nextCount = (diffData || []).find((x:number)=>!findDirectionData.skippedDetails.includes(x));
            } else if(findDirectionData.skippedDetails && findDirectionData.skippedDetails.length){
              nextCount = (totalCount || []).find((x:number)=>!findDirectionData.skippedDetails.includes(x));
            }

            position = countData[j].position;
            directionId = findDirectionData.id;
            count = nextCount;
            isBreak = true;
            break;
          }
        }
        if (isBreak) break;
      }
    }
    return {
      position,
      directionId,
      count,
    };
  };

  private setDirections = () => {
    (this.directionCounts || []).map((data, index) => {
      const findIndx = this.allDirections.find(
        (x) => x.id === data.direction.id
      );
      if (findIndx === undefined) {
        this.allDirections.push({
          id: data.direction.id,
          name: data.direction.name,
          order: data.direction.order
        });
      }
    });
  };

  // Set direction count from Measurement Hub
  public setDirectionCount(
    positionId: any,
    directionId: any,
    compCount: number
  ): void {
    let countData = this.countData?.find((x) => x.position === positionId);
    let direction = countData?.directions?.find((x) => x.id === directionId);
    if (direction) {
      direction.completedCounts = compCount;
    }
    let completedCountsForPosition = 0;
    countData?.directions.forEach((x: any) => {
      if (x.name !== "All") {
        completedCountsForPosition += x.completedCounts;
      }
    });
    let allDirection = countData?.directions?.find((x) => x.name === "All");
    if (allDirection) {
      allDirection.completedCounts = completedCountsForPosition;
    }
  }

  // Set Position count from Measurement Hub
  public setPositionCount(positionCount: IPositionCount[]): void {
    this.positionCount = positionCount;
  }

  private setPosData() {
    if (this.positionCount.length > 0) {
      this.positionCount = [];
      const readingData: any[] = [];

      const unique = Array.from(new Set(this.readings));
      const uniqueValues = unique.filter(
        (element, index) =>
          unique.findIndex(
            (obj) =>
              obj.propertyId === element.propertyId &&
              obj.position === element.position
          ) === index
      );
      uniqueValues.forEach((data, index) => {
        const posCount = this.oldPositionCount.find(
          (x) => x.position === data.position
        );
        let selected = false;
        if (posCount.index !== undefined) {
          selected = posCount.index === 0 ? true : false;
        } else {
          selected = index === 0 ? true : false;
        }
        const positionCount = {
          position: data.position,
          totalCounts: posCount.totalCounts,
          completedCounts: posCount.completedCounts,
          selected,
          index,
        };
        this.positionCount.push(positionCount);
      });
    }
  }

  private setAllDirections() {
    if (this.directionCounts?.length > 0) {
      this.countData = [];
    }
    const maxCountMapping: Dictionary<number> = {};
    const maxCountMappingNonAutoline: maxCountMappingNonAutoline[] = [];
    (this.directionCounts || []).forEach(
      (entity: IDirectionCount, index: number) => {
        if (this.isAutoLine) {
          if (maxCountMapping[entity.direction.id] === undefined) {
            maxCountMapping[entity.direction.id] = entity.totalCounts;
          } else {
            maxCountMapping[entity.direction.id] = Math.max(
              maxCountMapping[entity.direction.id],
              entity.totalCounts
            );
          }

          const direction = {
            id: entity.direction.id,
            name: entity.direction.name,
            order: entity.direction.order,
            completedCounts: entity.completedCounts,
            totalCounts: entity.totalCounts,
            skipped: entity?.skipped ?? 0,
            selected: false,
            maxCount: maxCountMapping[entity.direction.id],
            skippedDetails :entity.skippedDetails.length
                  ? entity.skippedDetails.split(",").map((x:string) => +x)
                  : [],
            measurementCounts: entity.measurementCounts?.length ? 
                  entity.measurementCounts.split(",").map((x:string) => +x) : []
          };

          const allId = "All_" + entity.position + "_" + this.id;
          if (maxCountMapping[allId] === undefined) {
            maxCountMapping[allId] = entity.totalCounts;
          } else {
            maxCountMapping[allId] = Math.max(
              maxCountMapping[allId],
              entity.totalCounts
            );
          }

          const allDirection = {
            id: allId,
            name: "All",
            order:0,
            completedCounts: entity.completedCounts,
            totalCounts: entity.totalCounts,
            skipped: entity?.skipped ?? 0,
            selected: true,
            // TODO: Need to handle blank columns later
            maxCount: this.isAutoLine
              ? maxCountMapping[entity.direction.id]
              : maxCountMapping[allId],
            skippedDetails :entity.skippedDetails.length
                  ? entity.skippedDetails.split(",").map((x:string) => +x)
                  : [],
            measurementCounts : entity.measurementCounts?.length ? 
            entity.measurementCounts.split(",").map((x:string) => +x) : []
          };

          const count = {
            position: entity.position,
            index: index,
            positionName: entity.name,
            directions: [allDirection, direction],
            selected: index === 0 ? true : false,
          };
          const pos = this.countData.find(
            (obj) =>
              obj.positionName === count.positionName &&
              obj.position === count.position
          );
          if (pos === undefined) {
            this.countData.push(count);
          }
        } else {
          if (this.countData.some((x) => x.position === entity.position)) {
            const count = this.countData.find(
              (x) => x.position === entity.position
            );

            let findindexMapData = -1;
            findindexMapData = maxCountMappingNonAutoline.findIndex(
              (x: maxCountMappingNonAutoline) =>
                x.id === entity.direction.id && x.position === entity.position
            );
            if (findindexMapData === -1) {
              maxCountMappingNonAutoline.push({
                id: entity.direction.id,
                position: entity.position,
                maxCount: entity.totalCounts,
              });
            } else {
              maxCountMappingNonAutoline[findindexMapData].maxCount = Math.max(
                maxCountMappingNonAutoline[findindexMapData].maxCount,
                entity.totalCounts
              );
            }

            findindexMapData = maxCountMappingNonAutoline.findIndex(
              (x: maxCountMappingNonAutoline) =>
                x.id === entity.direction.id && x.position === entity.position
            );

            if (count) {
              count.directions.push({
                id: entity.direction.id,
                name: entity.direction.name,
                order: entity.direction.order,
                completedCounts: entity.completedCounts,
                totalCounts: entity.totalCounts,
                selected: false,
                maxCount: maxCountMappingNonAutoline[findindexMapData].maxCount,
                skipped: entity?.skipped ?? 0,
                skippedDetails: entity.skippedDetails.length
                  ? entity.skippedDetails.split(",").map((x:string) => +x)
                  : [],
                measurementCounts: entity.measurementCounts?.length ? 
                  entity.measurementCounts.split(",").map((x:string) => +x) : []
              });
            }

            const allDirection = count.directions.find(
              (x) => x.id === "All_" + entity.position + "_" + this.id
            );
            allDirection.completedCounts += entity.completedCounts;
            allDirection.totalCounts += entity.totalCounts;
            allDirection.skipped += entity?.skipped ?? 0;
            allDirection.skippedDetails =[];
            allDirection.measurementCounts =[];
            const allId = "All_" + entity.position + "_" + this.id;
            let findindexAllMapData = maxCountMappingNonAutoline.findIndex(
              (x: maxCountMappingNonAutoline) =>
                x.id === allId && x.position === entity.position
            );
            if (entity.totalCounts !== 0) {
              allDirection.maxCount = Math.max(
                maxCountMappingNonAutoline[findindexMapData].maxCount,
                maxCountMappingNonAutoline[findindexAllMapData].maxCount
              );
            }
          } else {
            let findindexMapData = -1;
            findindexMapData = maxCountMappingNonAutoline.findIndex(
              (x: maxCountMappingNonAutoline) =>
                x.id === entity.direction.id && x.position === entity.position
            );
            if (findindexMapData === -1) {
              maxCountMappingNonAutoline.push({
                id: entity.direction.id,
                position: entity.position,
                maxCount: entity.totalCounts,
              });
            } else {
              maxCountMappingNonAutoline[findindexMapData].maxCount = Math.max(
                maxCountMappingNonAutoline[findindexMapData].maxCount,
                entity.totalCounts
              );
            }

            findindexMapData = maxCountMappingNonAutoline.findIndex(
              (x: maxCountMappingNonAutoline) =>
                x.id === entity.direction.id && x.position === entity.position
            );
            const direction = {
              id: entity.direction.id,
              name: entity.direction.name,
              order:entity.direction.order,
              completedCounts: entity.completedCounts,
              totalCounts: entity.totalCounts,
              skipped: entity?.skipped ?? 0,
              selected: false,
              maxCount: maxCountMappingNonAutoline[findindexMapData].maxCount,
              skippedDetails :entity.skippedDetails.length
                  ? entity.skippedDetails.split(",").map((x:string) => +x)
                  : [],
              measurementCounts: entity.measurementCounts?.length ? 
                  entity.measurementCounts.split(",").map((x:string) => +x) : []
            };

            const allId = "All_" + entity.position + "_" + this.id;
            let findindexAllMapData = -1;
            findindexAllMapData = maxCountMappingNonAutoline.findIndex(
              (x: maxCountMappingNonAutoline) =>
                x.id === allId && x.position === entity.position
            );
            if (findindexAllMapData === -1) {
              if (entity.totalCounts === 0) {
                maxCountMappingNonAutoline.push({
                  id: allId,
                  position: entity.position,
                  maxCount:
                    maxCountMappingNonAutoline[findindexMapData].maxCount,
                });
              } else {
                maxCountMappingNonAutoline.push({
                  id: allId,
                  position: entity.position,
                  maxCount: entity.totalCounts,
                });
              }
            } else {
              maxCountMappingNonAutoline[findindexAllMapData].maxCount =
                Math.max(
                  maxCountMappingNonAutoline[findindexAllMapData].maxCount,
                  entity.totalCounts
                );
            }
            findindexAllMapData = maxCountMappingNonAutoline.findIndex(
              (x: maxCountMappingNonAutoline) =>
                x.id === allId && x.position === entity.position
            );

            const allDirection = {
              id: allId,
              name: "All",
              order:0,
              completedCounts: entity.completedCounts,
              skipped: entity?.skipped ?? 0,
              skippedDetails :entity.skippedDetails.length
                  ? entity.skippedDetails.split(",").map((x:string) => +x)
                  : [],
              measurementCounts: entity.measurementCounts?.length ? 
                  entity.measurementCounts.split(",").map((x:string) => +x) : [],
              totalCounts: entity.totalCounts,
              selected: true,
              // TODO: Need to handle blank columns later
              maxCount: this.isAutoLine
                ? maxCountMappingNonAutoline[findindexMapData].maxCount
                : maxCountMappingNonAutoline[findindexAllMapData].maxCount,
            };

            const count = {
              position: entity.position,
              index: index,
              positionName: entity.name,
              directions: [allDirection, direction],
              selected: false
            };
            const pos = this.countData.find(
              (obj) =>
                obj.positionName === count.positionName &&
                obj.position === count.position
            );
            if (pos === undefined) {
              this.countData.push(count);
            }
          }
        }
      }
    );
    this.countData[0].selected = true;
  }

  public setCount(count: ICount) {
    this.count = count;
    this.completionPercentage =
      (count.completedCounts / count.totalCounts) * 100 + "%";
  }

  public setAutolineCount(count: ICount) {
    const countData = {
      completedCounts: this.count.completedCounts + count.completedCounts,
      totalCounts: this.count.totalCounts + count.totalCounts,
    };
    this.count = countData;
    this.completionPercentage =
      (this.count.completedCounts / this.count.totalCounts) * 100 + "%";
  }

  public setSelectedDirection(position: number, id: string) {
    const countData = this.countData.find((x) => x.position === position);
    const prevSelectedDirection = countData.directions.find((y) => y.selected);
    if (prevSelectedDirection !== undefined) {
      prevSelectedDirection.selected = false;
    }
    const toBeSelectedDirection = countData.directions.find((x) => x.id === id);
    this.countData.forEach((element) => {
      element.directions.forEach((dir) => {
        // reset all to flase
        dir.selected = false;
      });
      let direction;
      if (toBeSelectedDirection?.id.indexOf("All_") === -1) {
        direction = element.directions.find(
          (x) => x.id === toBeSelectedDirection?.id
        );
      } else {
        direction = element.directions.find(
          (x) => x.id.indexOf(toBeSelectedDirection?.id) === -1
        );
      }
      if (direction != null && direction != undefined) {
        // set selected to all positions
        direction.selected = true;
      }
    });
    if (toBeSelectedDirection !== undefined) {
      toBeSelectedDirection.selected = true;
    }
  }
  public updateDirectionCount(
    position: number,
    directionId: string,
    completedCounts: number
  ) {
    const directionCount = this.directionCounts.find(
      (x) => x.direction.id === directionId && x.position === position
    );
    const directionCountsIndex = this.directionCounts.findIndex(
      (x) => x.direction.id === directionId && x.position === position
    );
    if (directionCount !== undefined && this.isAutoLine) {
      directionCount.completedCounts = completedCounts;
      this.directionCounts[directionCountsIndex] = directionCount;
      this.setAllDirections();
    }
  }

  public setStatus(instrumentStatus: IStatus, instrumentSampleStatus: IStatus) {
    // NotMeasure = ABB_PLAY
    // measuring = stop
    // pending = play
    if (instrumentStatus !== undefined) {
      if (instrumentStatus?.name === Status.NOTMEASURED) {
        this.status = { name: "Not Measured", color: "black" };
        this.iconStatus = { name: ABB_PLAY, color: "blue" };
      } else if (instrumentStatus?.name === Status.MEASURING) {
        this.status = { name: "Measuring", color: "blue" };
        this.iconStatus = { name: ABB_STOP, color: "red" };
      } else if (instrumentStatus?.name === Status.STOPPED) {
        this.status = { name: "Stopped", color: "blue" };
        this.iconStatus = { name: ABB_REFRESH, color: "blue" };
      } else if (instrumentStatus?.name === Status.INUSE) {
        this.status = { name: "Waiting", color: "black" };
        this.iconStatus = { name: ABB_WAITING, color: "blue" };
      } else if (
        instrumentStatus?.name === Status.PENDING &&
        instrumentSampleStatus?.name === Status.INUSE
      ) {
        this.status = { name: "Waiting", color: "black" };
        this.iconStatus = { name: ABB_WAITING, color: "blue" };
      } else if (instrumentStatus?.name === Status.PENDING) {
        this.status = { name: "Pending", color: "black" };
        this.iconStatus = { name: ABB_PLAY, color: "blue" };
      } else if (instrumentStatus?.name === Status.PAUSED) {
        this.status = { name: "Paused", color: "black" };
        this.iconStatus = { name: ABB_PLAY, color: "blue" };
      } else if (instrumentStatus?.name === Status.AVAILABLE) {
        this.status = { name: "Available", color: "black" };
        this.iconStatus = { name: ABB_PLAY, color: "blue" };
      } else if (instrumentStatus?.name === Status.COMPLETED) {
        this.status = { name: "Completed", color: "blue" };
        this.iconStatus = { name: ABB_REFRESH, color: "blue" };
      } else {
        this.status = { name: "Completed", color: "blue" };
        this.iconStatus = { name: ABB_REFRESH, color: "blue" };
      }
    }
  }

  public setCurrentMeasuringProperty(propertyId: string, position: number) {
    this.currentMeasuringProperty = propertyId;
    this.currentMeasuringPosition = position;
  }

  public setAverage(
    propertyId: string,
    directionId: string,
    position: number,
    statistic: any
  ) {
    this.statistics.push({
      covariance: statistic?.covariance || 0,
      directionId,
      mean: statistic?.mean,
      median: statistic?.median,
      position,
      propertyId,
      standardDeviation: statistic?.standardDeviation,
    });
  }

  public setPosition(index: number) {
    const prevPositionCount = this.positionCount.find(
      (x) => x.selected === true
    );
    if (prevPositionCount !== undefined) {
      prevPositionCount.selected = false;
    }
    const toBeSelectedPosition = this.positionCount.find(
      (x) => x.position === index
    );
    if (toBeSelectedPosition !== undefined) {
      toBeSelectedPosition.selected = true;
    }

    const prevCountData = this.countData.find((x) => x.selected === true);
    if (prevCountData !== undefined) {
      prevCountData.selected = false;
    }
    const toBeSelectedDirection = this.countData.find(
      (x) => x.position === index
    );
    if (toBeSelectedDirection !== undefined) {
      toBeSelectedDirection.selected = true;
    }
  }

  public setReadings(
    count: any,
    directionId: string,
    position: number,
    propertyId: string,
    value: any,
    valueRange: IValueRange,
    positionName?: string,
    decimals?: number
  ) {
    this.readings.push({
      count,
      directionId,
      position,
      propertyId,
      value,
      valueRange,
      positionName,
      decimals,
    });
  }

  public setMeasuredPosition(position: number) {
    this.setPositionVal = position;
    if (this.isAutoLine) {
      const prevPositionCount = this.positionCount.find(
        (x) => x.selected === true
      );
      if (prevPositionCount !== undefined) {
        prevPositionCount.selected = false;
      }
      const toBeSelectedPosition = this.positionCount.find(
        (x) => x.position === position
      );
      if (toBeSelectedPosition !== undefined) {
        toBeSelectedPosition.selected = true;
      }
      const prevCountData = this.countData.find((x) => x.selected === true);
      if (prevCountData !== undefined) {
        prevCountData.selected = false;
      }
      const toBeSelectedDirection = this.countData.find(
        (x) => x.position === position
      );
      if (toBeSelectedDirection !== undefined) {
        toBeSelectedDirection.selected = true;
      }
    } else {
      const prevPositionCount = this.positionCount.find(
        (x) => x.selected === true
      );
      if (prevPositionCount !== undefined) {
        prevPositionCount.selected = false;
      }
      const toBeSelectedPosition = this.positionCount.find(
        (x) => x.position === position
      );
      if (toBeSelectedPosition !== undefined) {
        toBeSelectedPosition.selected = true;
      }

      const prevCountData = this.countData.find((x) => x.selected === true);
      if (prevCountData !== undefined) {
        prevCountData.selected = false;
      }
      const toBeSelectedDirection = this.countData.find(
        (x) => x.position === position
      );
      if (toBeSelectedDirection !== undefined) {
        toBeSelectedDirection.selected = true;
      }
    }
  }
}

const defaultInstrumentDetails = new InstrumentDetails(
  "",
  "",
  { completedCounts: 0, totalCounts: 0 },
  [],
  [],
  [],
  { id: "", name: "" },
  [],
  [],
  [],
  [],
  false,
  "",
  null,
  null,
  { id: "", name: "" },
);
export { defaultInstrumentDetails };
